import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { arrayRemove } from '@datorama/akita';
import { map, tap } from 'rxjs';
import {
  ActionTrackings,
  ActionTrackingsResponse,
  CustomEventTracking,
  EventTracking,
  StandardEventTracking
} from './action-tracking.model';
import { ActionTrackingsQuery } from './action-tracking.query';
import { ActionTrackingsStore } from './action-tracking.store';

@Injectable({
  providedIn: 'root'
})
export class ActionTrackingsService {
  constructor(
    private http: HttpClient,
    private store: ActionTrackingsStore,
    private query: ActionTrackingsQuery
  ) {}

  getActionTrackingsTxn() {
    return this.http.get<ActionTrackingsResponse>(`inbox/private/v1/action_tracking/_listTxn`).pipe(
      map(trackings => {
        const transformedData: ActionTrackings[] = [];
        Object.keys(trackings).forEach(event => {
          const txnUuids = trackings[event as EventTracking];
          txnUuids.forEach(txnUuid => {
            const existingData = transformedData.find(data => data.txnUuid === txnUuid);
            if (existingData) {
              existingData.actionTrackings.push(event as EventTracking);
            } else {
              transformedData.push({
                txnUuid,
                actionTrackings: [event as EventTracking]
              });
            }
          });
        });

        // remove watched if there is no custom event tracking
        transformedData.forEach(data => {
          if (data.actionTrackings.includes(StandardEventTracking.watched)) {
            const hasCustomEventTracking = data.actionTrackings.some(action =>
              Object.values(CustomEventTracking).includes(action as CustomEventTracking)
            );

            if (!hasCustomEventTracking) {
              data.actionTrackings = data.actionTrackings.filter(action => action !== StandardEventTracking.watched);
            }
          }
        });

        // remove all action trackings if there is no standard event tracking
        return transformedData.filter(data =>
          data.actionTrackings.some(action =>
            Object.values(StandardEventTracking).includes(action as StandardEventTracking)
          )
        );
      }),
      tap(transformedData => {
        transformedData.forEach(data => {
          this.store.upsert(data.txnUuid, { actionTrackings: data.actionTrackings });
          this.store.ui.update(data.txnUuid, {
            loaded: true
          });
        });
      })
    );
  }

  deleteActionTrackingByTxn(txnUuid: string) {
    return this.http.delete(`inbox/private/v1/action_tracking/${txnUuid}`).pipe(
      tap(() => {
        this.removeTracking(txnUuid);
      })
    );
  }

  addActionTrackingToStore(txnUuid: string, events: EventTracking | EventTracking[]) {
    const entity = this.query.getEntity(txnUuid);
    const eventList = Array.isArray(events) ? events : [events];
    if (entity) {
      const newEvents = eventList.filter(event => entity.actionTrackings.some(e => e !== event));
      if (newEvents.length > 0) {
        this.store.update(txnUuid, ({ actionTrackings }) => ({
          actionTrackings: [...actionTrackings, ...newEvents]
        }));
      }
    } else {
      const newActionTracking: ActionTrackings = {
        txnUuid,
        actionTrackings: eventList
      };
      this.store.upsertMany([newActionTracking]);
    }
  }

  removeActionTrackingToStore(txnUuid: string, event: EventTracking) {
    this.store.update(txnUuid, ({ actionTrackings }) => ({
      actionTrackings: arrayRemove(actionTrackings, event)
    }));
    return true;
  }

  removeTracking(txnUuid: string) {
    this.store.remove(txnUuid);
  }
}
